// Multilingual translations for The China Explorer site

export const LANGUAGES = {
  en: { label: "EN", name: "English", dir: "ltr" },
  zh: { label: "中文", name: "中文", dir: "ltr" },
  ar: { label: "العربية", name: "العربية", dir: "rtl" },
  ru: { label: "RU", name: "Русский", dir: "ltr" },
  fr: { label: "FR", name: "Français", dir: "ltr" },
  es: { label: "ES", name: "Español", dir: "ltr" },
  de: { label: "DE", name: "Deutsch", dir: "ltr" },
};

export const translations = {
  en: {
    nav: {
      destinations: "Destinations",
      culture: "Culture",
      cuisine: "Cuisine",
      festivals: "Festivals",
      tips: "Travel Tips",
      visa: "Visa",
      guide: "Travel Guide",
      services: "Services",
      cantonFair: "Canton Fair",
      itinerary: "Plan Trip",
      book: "Book",
      contact: "Contact",
      whyUs: "Why Us",
      experiences: "Experiences",
      education: "Education",
      faq: "FAQ",

    },
    hero: {
      eyebrow: "Discover the Middle Kingdom",
      title: "An Ancient Civilization,\nA Modern Wonder.",
      subtitle:
        "From the misty karsts of Guilin to the neon skyline of Shanghai — let five thousand years of culture unfold before you.",
      cta_primary: "Explore Destinations",
      cta_secondary: "Plan My Trip",
    },
    destinations: {
      eyebrow: "Iconic Destinations",
      title: "Where Legends Take Shape",
      items: [
        { name: "The Great Wall", city: "Beijing", blurb: "A 13,000-mile dragon of stone winding through history." },
        { name: "Forbidden City", city: "Beijing", blurb: "500 years of imperial secrets behind crimson walls." },
        { name: "Guilin & Li River", city: "Guangxi", blurb: "Misty karst mountains that inspired ink paintings." },
        { name: "Shanghai Bund", city: "Shanghai", blurb: "Where colonial elegance meets a futuristic skyline." },
        { name: "Terracotta Army", city: "Xi'an", blurb: "An emperor's silent army, eight thousand strong." },
        { name: "Zhangjiajie", city: "Hunan", blurb: "Floating sandstone pillars from the heart of Avatar." },
      ],
    },
    culture: {
      eyebrow: "Cultural Heritage",
      title: "The Art of Living, Refined Over Millennia",
      intro:
        "Calligraphy, tea, opera, kung fu, silk — every tradition is a doorway into Chinese philosophy and grace.",
      pillars: [
        { name: "Tea Ceremony", body: "Sip from cups of jasmine, pu'er and longjing in centuries-old teahouses." },
        { name: "Calligraphy", body: "The dance of brush, ink, and rice paper — meditation made visible." },
        { name: "Kung Fu", body: "Train where Shaolin monks first turned discipline into living art." },
        { name: "Peking Opera", body: "Painted masks, soaring voices, and stories that thunder across dynasties." },
      ],
    },
    cuisine: {
      eyebrow: "Cuisine Highlights",
      title: "Eight Great Traditions, One Endless Feast",
      items: [
        { name: "Sichuan", flavor: "Numbing & spicy", dish: "Mapo Tofu" },
        { name: "Cantonese", flavor: "Delicate & fresh", dish: "Dim Sum" },
        { name: "Beijing", flavor: "Imperial & smoky", dish: "Peking Duck" },
        { name: "Shanghainese", flavor: "Sweet & savory", dish: "Xiaolongbao" },
        { name: "Hunan", flavor: "Bold & fiery", dish: "Chairman's Pork" },
        { name: "Xinjiang", flavor: "Aromatic & grilled", dish: "Lamb Skewers" },
      ],
    },
    festivals: {
      eyebrow: "Festivals & Traditions",
      title: "A Year Lit by Lanterns",
      items: [
        { name: "Spring Festival", when: "Jan – Feb", body: "Red lanterns, dumplings and fireworks usher in the lunar new year." },
        { name: "Lantern Festival", when: "Feb", body: "Sky lanterns drift over ancient cities at the close of new year celebrations." },
        { name: "Dragon Boat", when: "June", body: "Painted boats race rivers to the beat of thundering drums." },
        { name: "Mid-Autumn", when: "Sept", body: "Families share mooncakes beneath a harvest moon." },
      ],
    },
    tips: {
      eyebrow: "For Foreign Travelers",
      title: "Travel China, Effortlessly",
      items: [
        { title: "Visa", body: "Many nationalities now enjoy 144-hour visa-free transit; check the latest with the Chinese embassy." },
        { title: "Payments", body: "Alipay & WeChat Pay accept foreign cards — set them up before you fly." },
        { title: "Trains", body: "China's bullet trains link 550+ cities. Book directly with The China Explorer — we secure better rates and bilingual support." },
        { title: "Language", body: "Download a translation app; English is common in airports and luxury hotels." },
        { title: "Connectivity", body: "Pick up an eSIM or buy a local SIM at the airport — and a VPN for familiar apps." },
        { title: "Etiquette", body: "Greet with a smile, accept business cards with both hands, and never stick chopsticks upright." },
      ],
    },
    canton: {
      eyebrow: "Business Travel",
      title: "The Canton Fair Package",
      subtitle:
        "China's largest trade exhibition, twice a year in Guangzhou. We craft turnkey trips for buyers and exhibitors.",
      bullets: [
        "Airport transfers & 4–5★ hotels near Pazhou",
        "Pre-scheduled supplier meetings",
        "Bilingual interpreters (EN / 中文 / عربي)",
        "Factory visits in the Pearl River Delta",
      ],
      cta: "Request a Canton Fair Quote",
    },
    services: {
      eyebrow: "Specialty Trips",
      title: "More Than Tourism",
      subtitle:
        "Beyond leisure travel, The China Explorer designs purpose-built journeys for healthcare, learning and business.",
      cta: "Inquire",
      items: [
        {
          name: "Medical Trip",
          body: "World-class hospitals and traditional medicine retreats — from advanced cardiology in Beijing to acupuncture and herbal therapy in Hangzhou.",
          features: [
            "JCI-accredited hospital partnerships",
            "Arabic-speaking medical liaisons",
            "Visa, recovery hotel & companion support",
          ],
        },
        {
          name: "Mandarin Language Trip",
          body: "Immersive Mandarin programs in Beijing, Shanghai and Chengdu — perfect for students, executives or curious explorers.",
          features: [
            "Group, private or business-tailored classes",
            "Homestay or campus accommodation",
            "HSK exam preparation available",
          ],
        },
        {
          name: "Business Meetings",
          body: "End-to-end B2B trips for negotiations, supplier audits, factory tours and trade-show coverage — outside the Canton Fair calendar.",
          features: [
            "Pre-vetted manufacturer matching",
            "Bilingual translators & contract liaison",
            "Logistics across Pearl River & Yangtze Delta",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "AI Itinerary Builder",
      title: "Your China Trip, Designed in Seconds",
      subtitle: "Tell us your dates, interests and budget — Mei, our AI concierge, drafts a day-by-day plan.",
      duration: "Duration (days)",
      travelers: "Travelers",
      arrival: "Arrival city",
      budget: "Budget",
      budgets: { budget: "Budget", mid: "Mid-range", luxury: "Luxury" },
      interests: "Interests",
      interestOptions: ["History", "Food", "Nature", "Modern Cities", "Shopping", "Spiritual", "Adventure", "Family"],
      notes: "Anything else? (optional)",
      submit: "Generate My Itinerary",
      generating: "Crafting your journey...",
      result: "Your itinerary",
      reset: "Start over",
    },
    contact: {
      eyebrow: "Get In Touch",
      title: "Speak With a China Travel Curator",
      subtitle: "Bespoke trips, group tours, business travel — write to us and we'll respond within 24 hours.",
      name: "Full name",
      email: "Email address",
      subject: "Subject",
      message: "Your message",
      submit: "Send Inquiry",
      success: "Thank you. Mei will be in touch shortly.",
      error: "Something went wrong. Please try again.",
    },
    chat: {
      title: "Ask Mei",
      subtitle: "AI Travel Concierge",
      placeholder: "Where should I go? What should I eat?",
      send: "Send",
      welcome: "Ni hao! I'm Mei, your guide to China. Ask me about destinations, food, culture, or let me build you an itinerary.",
    },
    footer: {
      tagline: "The China Explorer — Five Thousand Years of Wonder, Your Personal Guide.",
      rights: "© 2026 The China Explorer. All rights reserved.",
    },
  },

  zh: {
    nav: {
      destinations: "目的地",
      culture: "文化",
      cuisine: "美食",
      festivals: "节日",
      tips: "旅行贴士",
      visa: "签证",
      guide: "出行指南",
      services: "专项服务",
      cantonFair: "广交会",
      itinerary: "规划行程",
      book: "立即预订",
      contact: "联系我们",
      whyUs: "为何选我们",
      experiences: "精选体验",
      education: "教育项目",
      faq: "常见问题",

    },
    hero: {
      eyebrow: "发现中华大地",
      title: "古老文明,\n现代奇迹。",
      subtitle: "从桂林的烟雨喀斯特到上海的霓虹天际线 —— 让五千年文化在你眼前徐徐展开。",
      cta_primary: "探索目的地",
      cta_secondary: "规划我的旅行",
    },
    destinations: {
      eyebrow: "标志性目的地",
      title: "传奇诞生之处",
      items: [
        { name: "长城", city: "北京", blurb: "蜿蜒一万三千里的石龙,穿越千年历史。" },
        { name: "故宫", city: "北京", blurb: "朱红宫墙背后五百年的帝王秘事。" },
        { name: "桂林漓江", city: "广西", blurb: "烟雨中的喀斯特山峰,水墨画的灵感源泉。" },
        { name: "上海外滩", city: "上海", blurb: "殖民优雅与未来天际线在此交汇。" },
        { name: "兵马俑", city: "西安", blurb: "皇帝沉默的军团,八千将士永世守护。" },
        { name: "张家界", city: "湖南", blurb: "悬浮的砂岩石柱,《阿凡达》的真实灵感。" },
      ],
    },
    culture: {
      eyebrow: "文化遗产",
      title: "千年沉淀的生活艺术",
      intro: "书法、茶道、戏曲、功夫、丝绸 —— 每一种传统都是通向中国哲学与优雅的大门。",
      pillars: [
        { name: "茶道", body: "在百年茶馆中品味茉莉、普洱与龙井。" },
        { name: "书法", body: "毛笔、墨与宣纸的舞蹈 —— 看得见的禅意。" },
        { name: "功夫", body: "在少林寺习武,将纪律化为活的艺术。" },
        { name: "京剧", body: "彩绘脸谱、激昂唱腔,讲述跨越王朝的故事。" },
      ],
    },
    cuisine: {
      eyebrow: "美食精选",
      title: "八大菜系,无尽盛宴",
      items: [
        { name: "川菜", flavor: "麻辣鲜香", dish: "麻婆豆腐" },
        { name: "粤菜", flavor: "清淡新鲜", dish: "广式点心" },
        { name: "京菜", flavor: "皇家烟熏", dish: "北京烤鸭" },
        { name: "本帮菜", flavor: "甜咸交融", dish: "小笼包" },
        { name: "湘菜", flavor: "浓烈火辣", dish: "毛氏红烧肉" },
        { name: "新疆菜", flavor: "香气烧烤", dish: "羊肉串" },
      ],
    },
    festivals: {
      eyebrow: "节日与传统",
      title: "灯笼点亮的一年",
      items: [
        { name: "春节", when: "1–2月", body: "红灯笼、饺子和烟花迎来农历新年。" },
        { name: "元宵节", when: "2月", body: "孔明灯漂浮在古城上空,新年圆满落幕。" },
        { name: "端午节", when: "6月", body: "彩绘龙舟在鼓声中竞渡江河。" },
        { name: "中秋节", when: "9月", body: "家人共享月饼,共赏丰收明月。" },
      ],
    },
    tips: {
      eyebrow: "外国旅行者指南",
      title: "轻松畅游中国",
      items: [
        { title: "签证", body: "许多国家可享144小时过境免签,具体请咨询中国大使馆。" },
        { title: "支付", body: "支付宝和微信支付现已支持境外银行卡,出发前可提前绑定。" },
        { title: "高铁", body: "中国高铁连接550多个城市,直接通过 The China Explorer 预订 —— 更优价格、双语服务。" },
        { title: "语言", body: "下载翻译应用;机场和高端酒店常配英语服务。" },
        { title: "通讯", body: "购买eSIM或机场本地SIM卡,记得准备VPN访问熟悉的应用。" },
        { title: "礼仪", body: "微笑问候,双手递接名片,筷子不要竖插入饭。" },
      ],
    },
    canton: {
      eyebrow: "商务旅行",
      title: "广交会专属套餐",
      subtitle: "中国最大的贸易展览,每年两届在广州举办。我们为采购商与参展商打造一站式行程。",
      bullets: [
        "机场接送 & 琶洲附近4-5星酒店",
        "预约供应商会议",
        "双语翻译(中/英/阿)",
        "珠三角工厂考察",
      ],
      cta: "申请广交会报价",
    },
    services: {
      eyebrow: "专项行程",
      title: "不止旅游",
      subtitle: "The China Explorer 在休闲旅行之外,为医疗、学习与商务量身定制行程。",
      cta: "立即咨询",
      items: [
        {
          name: "医疗旅行",
          body: "世界级医院与中医康养 —— 从北京的尖端心脏科到杭州的针灸与草药疗法。",
          features: [
            "JCI 认证医院合作",
            "阿拉伯语医疗联络员",
            "签证、康复酒店与陪同服务",
          ],
        },
        {
          name: "中文语言之旅",
          body: "北京、上海、成都的沉浸式中文项目 —— 适合学生、商务人士与文化爱好者。",
          features: [
            "团体、一对一或商务定制课程",
            "寄宿家庭或校园住宿",
            "可安排 HSK 考试备考",
          ],
        },
        {
          name: "商务会议",
          body: "全程式 B2B 行程:谈判、供应商审核、工厂参观与展会考察 —— 不限于广交会期间。",
          features: [
            "预先筛选的制造商对接",
            "双语翻译与合同联络",
            "珠三角与长三角全程物流",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "AI 行程定制",
      title: "几秒钟,定制你的中国之旅",
      subtitle: "告诉我们日期、兴趣和预算 —— AI管家小美将为你起草每日行程。",
      duration: "天数",
      travelers: "出行人数",
      arrival: "抵达城市",
      budget: "预算",
      budgets: { budget: "经济", mid: "中档", luxury: "豪华" },
      interests: "兴趣",
      interestOptions: ["历史", "美食", "自然", "现代都市", "购物", "禅修", "探险", "亲子"],
      notes: "其他备注(可选)",
      submit: "生成我的行程",
      generating: "正在为你定制旅程...",
      result: "你的行程",
      reset: "重新开始",
    },
    contact: {
      eyebrow: "联系我们",
      title: "与中国旅行策划师对话",
      subtitle: "定制旅行、团体游、商务出行 —— 留言后24小时内回复。",
      name: "姓名",
      email: "邮箱",
      subject: "主题",
      message: "留言内容",
      submit: "发送咨询",
      success: "感谢你!小美稍后将与你联系。",
      error: "出错了,请重试。",
    },
    chat: {
      title: "问小美",
      subtitle: "AI 旅行管家",
      placeholder: "我该去哪里?吃什么?",
      send: "发送",
      welcome: "你好!我是小美,你的中国向导。问我目的地、美食、文化,或让我为你定制行程。",
    },
    footer: {
      tagline: "China Explorer —— 五千年的奇迹,您的专属向导。",
      rights: "© 2026 The China Explorer. 版权所有。",
    },
  },

  ar: {
    nav: {
      destinations: "الوجهات",
      culture: "الثقافة",
      cuisine: "المطبخ",
      festivals: "المهرجانات",
      tips: "نصائح السفر",
      visa: "التأشيرة",
      guide: "دليل السفر",
      services: "خدمات",
      cantonFair: "معرض كانتون",
      itinerary: "خطّط رحلتك",
      contact: "تواصل",
      whyUs: "لماذا نحن",
      experiences: "تجارب",
      education: "برامج تعليمية",
      faq: "الأسئلة",

    },
    hero: {
      eyebrow: "اكتشف المملكة الوسطى",
      title: "حضارة عريقة،\nأعجوبة عصرية.",
      subtitle: "من جبال قويلين الضبابية إلى ناطحات سحاب شنغهاي — دع خمسة آلاف عام من الثقافة تتكشّف أمامك.",
      cta_primary: "استكشف الوجهات",
      cta_secondary: "خطّط لرحلتي",
    },
    destinations: {
      eyebrow: "وجهات أيقونية",
      title: "حيث تتشكّل الأساطير",
      items: [
        { name: "سور الصين العظيم", city: "بكين", blurb: "تنين من الحجر يمتدّ 13000 ميل عبر التاريخ." },
        { name: "المدينة المحرّمة", city: "بكين", blurb: "خمسمئة عام من أسرار الأباطرة خلف جدران قرمزية." },
        { name: "قويلين ونهر لي", city: "قوانغشي", blurb: "جبال كارستية ضبابية ألهمت لوحات الحبر الصينية." },
        { name: "الباند، شنغهاي", city: "شنغهاي", blurb: "حيث تلتقي الأناقة الكولونيالية بالأفق المستقبلي." },
        { name: "جيش التراكوتا", city: "شيآن", blurb: "جيش صامت لإمبراطور، بثمانية آلاف جندي." },
        { name: "تشانغجياجيه", city: "هونان", blurb: "أعمدة حجرية معلّقة، ملهمة فيلم أفاتار." },
      ],
    },
    culture: {
      eyebrow: "التراث الثقافي",
      title: "فن العيش، صُقل عبر آلاف السنين",
      intro: "الخط، الشاي، الأوبرا، الكونغ فو، الحرير — كل تقليد بوّابة إلى الفلسفة والرقيّ الصيني.",
      pillars: [
        { name: "حفل الشاي", body: "احتسِ الياسمين والبو-إر واللونغجينغ في بيوت الشاي القديمة." },
        { name: "الخط الصيني", body: "رقصة الفرشاة والحبر والورق — تأمّل مرئيّ." },
        { name: "الكونغ فو", body: "تدرّب حيث حوّل رهبان شاولين الانضباط إلى فن حيّ." },
        { name: "أوبرا بكين", body: "أقنعة ملوّنة، أصوات مرتفعة، وحكايات تعبر السلالات." },
      ],
    },
    cuisine: {
      eyebrow: "أبرز المأكولات",
      title: "ثمانية مطابخ عظيمة، وليمة لا تنتهي",
      items: [
        { name: "سيتشوان", flavor: "حارّ ومخدّر", dish: "مابو توفو" },
        { name: "كانتوني", flavor: "خفيف وطازج", dish: "ديم سَم" },
        { name: "بكيني", flavor: "إمبراطوري ومدخّن", dish: "بطّة بكين" },
        { name: "شنغهاي", flavor: "حلو ومالح", dish: "شياو لونغ باو" },
        { name: "هونان", flavor: "جريء وناريّ", dish: "لحم الزعيم" },
        { name: "شينجيانغ", flavor: "عطري ومشويّ", dish: "أسياخ الضأن" },
      ],
    },
    festivals: {
      eyebrow: "المهرجانات والتقاليد",
      title: "عام تضيئه الفوانيس",
      items: [
        { name: "مهرجان الربيع", when: "يناير – فبراير", body: "فوانيس حمراء وزلابية وألعاب نارية تستقبل العام القمري." },
        { name: "مهرجان الفوانيس", when: "فبراير", body: "فوانيس سماوية تطفو فوق المدن القديمة في ختام احتفالات السنة الجديدة." },
        { name: "قارب التنين", when: "يونيو", body: "قوارب ملوّنة تتسابق على إيقاع الطبول الهادرة." },
        { name: "منتصف الخريف", when: "سبتمبر", body: "تتشارك العائلات كعك القمر تحت بدر الحصاد." },
      ],
    },
    tips: {
      eyebrow: "للمسافرين الأجانب",
      title: "تجوّل في الصين بسلاسة",
      items: [
        { title: "التأشيرة", body: "العديد من الجنسيات تستفيد الآن من ترانزيت 144 ساعة بدون تأشيرة — تأكّد من السفارة." },
        { title: "الدفع", body: "Alipay وWeChat Pay تقبلان البطاقات الأجنبية — فعّلهما قبل السفر." },
        { title: "القطارات", body: "تربط قطارات الرصاص أكثر من 550 مدينة. احجز مباشرة عبر The China Explorer — أسعار أفضل ودعم ثنائي اللغة." },
        { title: "اللغة", body: "حمّل تطبيق ترجمة؛ الإنجليزية شائعة في المطارات والفنادق الفاخرة." },
        { title: "الاتصال", body: "احصل على eSIM أو شريحة محلية في المطار — وVPN للتطبيقات المعتادة." },
        { title: "الإتيكيت", body: "حيِّ بابتسامة، تسلّم البطاقات بكلتا اليدين، ولا تغرز العيدان عمودياً." },
      ],
    },
    canton: {
      eyebrow: "السفر التجاري",
      title: "باقة معرض كانتون",
      subtitle: "أكبر معرض تجاري في الصين، مرّتين سنوياً في قوانغتشو. ننسّق رحلات متكاملة للمشترين والعارضين.",
      bullets: [
        "نقل من المطار وفنادق 4–5 نجوم قرب باجو",
        "اجتماعات موردين مرتّبة مسبقاً",
        "مترجمون ثنائيو اللغة (إنج / 中文 / عربي)",
        "زيارات مصانع في دلتا نهر اللؤلؤ",
      ],
      cta: "اطلب عرض سعر لمعرض كانتون",
    },
    services: {
      eyebrow: "رحلات متخصّصة",
      title: "أكثر من مجرّد سياحة",
      subtitle:
        "إلى جانب السفر الترفيهي، تصمّم The China Explorer رحلات متخصّصة للعلاج والتعلّم والأعمال.",
      cta: "استفسر الآن",
      items: [
        {
          name: "رحلات علاجية",
          body: "مستشفيات عالمية المستوى وعلاجات الطب الصيني التقليدي — من جراحة القلب المتقدّمة في بكين إلى الوخز بالإبر والأعشاب في هانغتشو.",
          features: [
            "شراكات مع مستشفيات معتمدة JCI",
            "منسّقون طبيون يتحدّثون العربية",
            "تأشيرة وفندق نقاهة ومرافق",
          ],
        },
        {
          name: "رحلات تعلّم لغة الماندرين",
          body: "برامج لغوية مكثّفة في بكين وشنغهاي وتشنغدو — مثالية للطلاب والمدراء والمستكشفين الفضوليين.",
          features: [
            "فصول جماعية أو خاصة أو لرجال الأعمال",
            "إقامة عائلية أو سكن جامعي",
            "إعداد لاختبار HSK متاح",
          ],
        },
        {
          name: "اجتماعات الأعمال",
          body: "رحلات B2B شاملة للمفاوضات وتدقيق الموردين وجولات المصانع ومعارض التجارة — خارج تقويم معرض كانتون.",
          features: [
            "مطابقة مع مصنّعين مدقّقين مسبقاً",
            "مترجمون ثنائيو اللغة ومنسّقو عقود",
            "لوجستيات في دلتا اللؤلؤ واليانغتسي",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "مُنشئ الرحلات بالذكاء الاصطناعي",
      title: "رحلتك إلى الصين، تُصمَّم في ثوانٍ",
      subtitle: "أخبرنا بتواريخك واهتماماتك وميزانيتك — سترسم لك ميي، مستشارتنا، خطة يومية.",
      duration: "المدة (أيام)",
      travelers: "عدد المسافرين",
      arrival: "مدينة الوصول",
      budget: "الميزانية",
      budgets: { budget: "اقتصادي", mid: "متوسط", luxury: "فاخر" },
      interests: "الاهتمامات",
      interestOptions: ["التاريخ", "الطعام", "الطبيعة", "المدن الحديثة", "التسوّق", "الروحانية", "المغامرة", "العائلة"],
      notes: "أي ملاحظات أخرى؟ (اختياري)",
      submit: "أنشئ خطّتي",
      generating: "نصمّم رحلتك...",
      result: "خطّتك",
      reset: "ابدأ من جديد",
    },
    contact: {
      eyebrow: "تواصل معنا",
      title: "تحدّث مع منسّق رحلات إلى الصين",
      subtitle: "رحلات مخصّصة، جولات جماعية، سفر تجاري — اكتب لنا وسنردّ خلال 24 ساعة.",
      name: "الاسم الكامل",
      email: "البريد الإلكتروني",
      subject: "الموضوع",
      message: "رسالتك",
      submit: "أرسل الاستفسار",
      success: "شكراً لك. ستتواصل ميي معك قريباً.",
      error: "حدث خطأ ما. حاول مرة أخرى.",
    },
    chat: {
      title: "اسأل ميي",
      subtitle: "مستشارة سفر بالذكاء الاصطناعي",
      placeholder: "إلى أين أذهب؟ ماذا آكل؟",
      send: "أرسل",
      welcome: "مرحباً! أنا ميي، دليلك إلى الصين. اسألني عن الوجهات أو الطعام أو الثقافة، أو دعني أصمّم خطّتك.",
    },
    footer: {
      tagline: "The China Explorer — خمسة آلاف عام من العجب، دليلك الشخصي.",
      rights: "© 2026 The China Explorer. جميع الحقوق محفوظة.",
    },
  },

  ru: {
    nav: {
      destinations: "Направления",
      culture: "Культура",
      cuisine: "Кухня",
      festivals: "Праздники",
      tips: "Советы",
      visa: "Виза",
      guide: "Гид",
      services: "Услуги",
      cantonFair: "Кантонская ярмарка",
      itinerary: "Маршрут",
      book: "Забронировать",
      contact: "Контакты",
      whyUs: "Почему мы",
      experiences: "Опыты",
      education: "Образование",
      faq: "FAQ",

    },
    hero: {
      eyebrow: "Откройте Срединное Царство",
      title: "Древняя цивилизация,\nсовременное чудо.",
      subtitle:
        "От туманных карстов Гуйлиня до неонового горизонта Шанхая — пять тысяч лет культуры раскроются перед вами.",
      cta_primary: "Открыть направления",
      cta_secondary: "Спланировать поездку",
    },
    destinations: {
      eyebrow: "Знаковые направления",
      title: "Где рождаются легенды",
      items: [
        { name: "Великая стена", city: "Пекин", blurb: "Каменный дракон длиной 21 000 км сквозь историю." },
        { name: "Запретный город", city: "Пекин", blurb: "500 лет имперских тайн за пурпурными стенами." },
        { name: "Гуйлинь и река Ли", city: "Гуанси", blurb: "Туманные карстовые горы, вдохновлявшие тушевую живопись." },
        { name: "Бунд, Шанхай", city: "Шанхай", blurb: "Колониальная элегантность встречает футуристический горизонт." },
        { name: "Терракотовая армия", city: "Сиань", blurb: "Безмолвное войско императора, восемь тысяч воинов." },
        { name: "Чжанцзяцзе", city: "Хунань", blurb: "Парящие песчаниковые столбы, вдохновившие «Аватар»." },
      ],
    },
    culture: {
      eyebrow: "Культурное наследие",
      title: "Искусство жить, отточенное тысячелетиями",
      intro:
        "Каллиграфия, чай, опера, кунг-фу, шёлк — каждая традиция открывает двери к китайской философии и изяществу.",
      pillars: [
        { name: "Чайная церемония", body: "Жасмин, пуэр и лунцзин в вековых чайных домах." },
        { name: "Каллиграфия", body: "Танец кисти, туши и рисовой бумаги — медитация воочию." },
        { name: "Кунг-фу", body: "Тренируйтесь там, где монахи Шаолиня превратили дисциплину в искусство." },
        { name: "Пекинская опера", body: "Расписные маски, парящие голоса и истории, гремящие сквозь династии." },
      ],
    },
    cuisine: {
      eyebrow: "Кулинарные открытия",
      title: "Восемь великих кухонь, бесконечный пир",
      items: [
        { name: "Сычуань", flavor: "Острый и онемелый", dish: "Мапо тофу" },
        { name: "Кантон", flavor: "Деликатный и свежий", dish: "Дим-сам" },
        { name: "Пекин", flavor: "Имперский и копчёный", dish: "Утка по-пекински" },
        { name: "Шанхай", flavor: "Сладкий и солёный", dish: "Сяолунбао" },
        { name: "Хунань", flavor: "Смелый и огненный", dish: "Свинина Мао" },
        { name: "Синьцзян", flavor: "Ароматный и жареный", dish: "Шашлык из ягнёнка" },
      ],
    },
    festivals: {
      eyebrow: "Праздники и традиции",
      title: "Год, освещённый фонарями",
      items: [
        { name: "Праздник Весны", when: "янв – фев", body: "Красные фонари, пельмени и фейерверки встречают лунный Новый год." },
        { name: "Праздник фонарей", when: "фев", body: "Небесные фонари парят над древними городами в финале новогодних торжеств." },
        { name: "Драконьи лодки", when: "июнь", body: "Расписные лодки мчатся по рекам под гром барабанов." },
        { name: "Середина осени", when: "сент", body: "Семьи делят лунные пряники под урожайной луной." },
      ],
    },
    tips: {
      eyebrow: "Для иностранных путешественников",
      title: "Путешествуйте по Китаю легко",
      items: [
        { title: "Виза", body: "Многие национальности получают 144-часовой безвизовый транзит — уточните в посольстве." },
        { title: "Платежи", body: "Alipay и WeChat Pay принимают иностранные карты — настройте их до вылета." },
        { title: "Поезда", body: "Скоростные поезда соединяют более 550 городов. Бронируйте напрямую через The China Explorer — лучшие цены и двуязычная поддержка." },
        { title: "Язык", body: "Установите приложение-переводчик; английский часто слышен в аэропортах и отелях люкс." },
        { title: "Связь", body: "Купите eSIM или локальную SIM в аэропорту — и VPN для привычных приложений." },
        { title: "Этикет", body: "Здоровайтесь с улыбкой, визитки принимайте двумя руками, палочки не втыкайте вертикально." },
      ],
    },
    canton: {
      eyebrow: "Бизнес-поездки",
      title: "Пакет Кантонской ярмарки",
      subtitle:
        "Крупнейшая торговая выставка Китая, дважды в год в Гуанчжоу. Готовые поездки для покупателей и экспонентов.",
      bullets: [
        "Трансфер и отели 4–5★ у Пачжоу",
        "Заранее организованные встречи с поставщиками",
        "Двуязычные переводчики (EN / 中文 / عربي)",
        "Посещения заводов в дельте Жемчужной реки",
      ],
      cta: "Запросить смету",
    },
    services: {
      eyebrow: "Специальные туры",
      title: "Больше, чем туризм",
      subtitle:
        "Помимо отдыха, The China Explorer создаёт целевые поездки: медицина, обучение, бизнес.",
      cta: "Запросить",
      items: [
        {
          name: "Медицинская поездка",
          body: "Клиники мирового уровня и традиционная медицина — от кардиологии в Пекине до иглоукалывания и фитотерапии в Ханчжоу.",
          features: [
            "Партнёрства с клиниками JCI",
            "Медицинские координаторы со знанием арабского",
            "Виза, отель для восстановления и сопровождение",
          ],
        },
        {
          name: "Изучение китайского",
          body: "Иммерсивные программы мандаринского в Пекине, Шанхае и Чэнду — для студентов, руководителей или любознательных путешественников.",
          features: [
            "Групповые, частные или бизнес-курсы",
            "Проживание в семье или кампусе",
            "Подготовка к HSK",
          ],
        },
        {
          name: "Деловые встречи",
          body: "Комплексные B2B поездки: переговоры, аудит поставщиков, экскурсии на заводы — вне дат Кантонской ярмарки.",
          features: [
            "Предварительно проверенные производители",
            "Двуязычные переводчики и юр-сопровождение",
            "Логистика в дельтах Жемчужной и Янцзы",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "AI-маршрут",
      title: "Ваша поездка в Китай за секунды",
      subtitle: "Сообщите даты, интересы и бюджет — Мэй, наш AI-консьерж, составит план по дням.",
      duration: "Длительность (дни)",
      travelers: "Путешественников",
      arrival: "Город прилёта",
      budget: "Бюджет",
      budgets: { budget: "Эконом", mid: "Средний", luxury: "Люкс" },
      interests: "Интересы",
      interestOptions: ["История", "Еда", "Природа", "Современные города", "Шопинг", "Духовное", "Приключения", "Семья"],
      notes: "Дополнительные пожелания (необяз.)",
      submit: "Создать маршрут",
      generating: "Создаём ваш маршрут...",
      result: "Ваш маршрут",
      reset: "Начать заново",
    },
    contact: {
      eyebrow: "Связаться",
      title: "Поговорите с куратором поездок в Китай",
      subtitle: "Индивидуальные туры, групповые, бизнес-поездки — мы ответим в течение 24 часов.",
      name: "Полное имя",
      email: "Электронная почта",
      subject: "Тема",
      message: "Ваше сообщение",
      submit: "Отправить запрос",
      success: "Спасибо! Мэй скоро свяжется с вами.",
      error: "Что-то пошло не так. Попробуйте ещё раз.",
    },
    chat: {
      title: "Спросить Мэй",
      subtitle: "AI-консьерж по путешествиям",
      placeholder: "Куда поехать? Что попробовать?",
      send: "Отправить",
      welcome: "Привет! Я Мэй, ваш проводник по Китаю. Спросите о направлениях, кухне, культуре — или попросите составить маршрут.",
    },
    footer: {
      tagline: "The China Explorer — пять тысяч лет чудес, ваш личный проводник.",
      rights: "© 2026 The China Explorer. Все права защищены.",
    },
  },

  fr: {
    nav: {
      destinations: "Destinations",
      culture: "Culture",
      cuisine: "Cuisine",
      festivals: "Festivals",
      tips: "Conseils",
      visa: "Visa",
      guide: "Guide",
      services: "Services",
      cantonFair: "Foire de Canton",
      itinerary: "Itinéraire",
      book: "Réserver",
      contact: "Contact",
      whyUs: "Pourquoi nous",
      experiences: "Expériences",
      education: "Éducation",
      faq: "FAQ",

    },
    hero: {
      eyebrow: "Découvrez l'Empire du Milieu",
      title: "Une civilisation ancienne,\nune merveille moderne.",
      subtitle:
        "Des karsts brumeux de Guilin aux gratte-ciel néon de Shanghai — laissez cinq mille ans de culture se dévoiler.",
      cta_primary: "Explorer les destinations",
      cta_secondary: "Planifier mon voyage",
    },
    destinations: {
      eyebrow: "Destinations emblématiques",
      title: "Où les légendes prennent forme",
      items: [
        { name: "La Grande Muraille", city: "Pékin", blurb: "Un dragon de pierre de 21 000 km serpentant à travers l'histoire." },
        { name: "Cité Interdite", city: "Pékin", blurb: "500 ans de secrets impériaux derrière les murs cramoisis." },
        { name: "Guilin & Rivière Li", city: "Guangxi", blurb: "Montagnes karstiques brumeuses qui ont inspiré la peinture à l'encre." },
        { name: "Le Bund, Shanghai", city: "Shanghai", blurb: "L'élégance coloniale rencontre un horizon futuriste." },
        { name: "Armée de terre cuite", city: "Xi'an", blurb: "L'armée silencieuse d'un empereur, huit mille soldats." },
        { name: "Zhangjiajie", city: "Hunan", blurb: "Piliers de grès flottants au cœur d'Avatar." },
      ],
    },
    culture: {
      eyebrow: "Patrimoine culturel",
      title: "L'art de vivre, raffiné sur des millénaires",
      intro:
        "Calligraphie, thé, opéra, kung-fu, soie — chaque tradition est une porte vers la philosophie et la grâce chinoises.",
      pillars: [
        { name: "Cérémonie du thé", body: "Jasmin, pu'er et longjing dans des maisons de thé séculaires." },
        { name: "Calligraphie", body: "La danse du pinceau, de l'encre et du papier de riz — la méditation visible." },
        { name: "Kung-fu", body: "Entraînez-vous où les moines de Shaolin ont transformé la discipline en art vivant." },
        { name: "Opéra de Pékin", body: "Masques peints, voix éclatantes, récits qui traversent les dynasties." },
      ],
    },
    cuisine: {
      eyebrow: "Cuisine d'exception",
      title: "Huit grandes traditions, un festin sans fin",
      items: [
        { name: "Sichuan", flavor: "Pimenté & engourdissant", dish: "Mapo tofu" },
        { name: "Cantonais", flavor: "Délicat & frais", dish: "Dim sum" },
        { name: "Pékin", flavor: "Impérial & fumé", dish: "Canard laqué" },
        { name: "Shanghai", flavor: "Sucré & salé", dish: "Xiaolongbao" },
        { name: "Hunan", flavor: "Audacieux & ardent", dish: "Porc du Président" },
        { name: "Xinjiang", flavor: "Aromatique & grillé", dish: "Brochettes d'agneau" },
      ],
    },
    festivals: {
      eyebrow: "Festivals & traditions",
      title: "Une année éclairée par les lanternes",
      items: [
        { name: "Fête du Printemps", when: "Jan – Fév", body: "Lanternes rouges, raviolis et feux d'artifice pour le Nouvel An lunaire." },
        { name: "Fête des Lanternes", when: "Fév", body: "Lanternes célestes flottant sur les villes anciennes." },
        { name: "Bateaux-dragons", when: "Juin", body: "Bateaux peints courant sur les rivières au rythme des tambours." },
        { name: "Mi-Automne", when: "Sept", body: "Familles partageant des gâteaux de lune sous la lune des moissons." },
      ],
    },
    tips: {
      eyebrow: "Pour les voyageurs étrangers",
      title: "Voyagez en Chine, sans effort",
      items: [
        { title: "Visa", body: "De nombreuses nationalités bénéficient désormais d'un transit sans visa de 144 h." },
        { title: "Paiements", body: "Alipay & WeChat Pay acceptent les cartes étrangères — configurez avant le départ." },
        { title: "Trains", body: "Les TGV relient plus de 550 villes. Réservez directement avec The China Explorer — meilleurs tarifs et accompagnement bilingue." },
        { title: "Langue", body: "Téléchargez une appli de traduction ; l'anglais est courant dans les aéroports et hôtels." },
        { title: "Connectivité", body: "Procurez-vous une eSIM ou une SIM locale — et un VPN pour vos applis habituelles." },
        { title: "Étiquette", body: "Saluez en souriant, recevez les cartes à deux mains, ne plantez jamais les baguettes." },
      ],
    },
    canton: {
      eyebrow: "Voyage d'affaires",
      title: "Le Pack Foire de Canton",
      subtitle:
        "La plus grande exposition commerciale de Chine, deux fois par an à Guangzhou. Voyages clés en main pour acheteurs et exposants.",
      bullets: [
        "Transferts aéroport & hôtels 4–5★ près de Pazhou",
        "Rendez-vous fournisseurs pré-organisés",
        "Interprètes bilingues (EN / 中文 / عربي)",
        "Visites d'usines dans le delta de la rivière des Perles",
      ],
      cta: "Demander un devis Foire de Canton",
    },
    services: {
      eyebrow: "Voyages spécialisés",
      title: "Au-delà du tourisme",
      subtitle:
        "The China Explorer conçoit des voyages sur mesure pour la santé, l'apprentissage et les affaires.",
      cta: "Demander",
      items: [
        {
          name: "Voyage médical",
          body: "Hôpitaux de classe mondiale et retraites de médecine traditionnelle — de la cardiologie à Pékin à l'acupuncture à Hangzhou.",
          features: [
            "Partenariats hôpitaux JCI",
            "Liaisons médicales arabophones",
            "Visa, hôtel de récupération & accompagnant",
          ],
        },
        {
          name: "Voyage linguistique mandarin",
          body: "Programmes immersifs à Pékin, Shanghai et Chengdu — pour étudiants, dirigeants ou explorateurs curieux.",
          features: [
            "Cours collectifs, privés ou sur mesure",
            "Famille d'accueil ou logement campus",
            "Préparation au HSK disponible",
          ],
        },
        {
          name: "Réunions d'affaires",
          body: "Voyages B2B complets : négociations, audits fournisseurs, visites d'usines — hors calendrier de la Foire de Canton.",
          features: [
            "Mise en relation avec fabricants vérifiés",
            "Traducteurs et liaisons contractuelles",
            "Logistique deltas Rivière des Perles & Yangtsé",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "Itinéraire IA",
      title: "Votre voyage en Chine, conçu en quelques secondes",
      subtitle: "Indiquez vos dates, intérêts et budget — Mei, notre concierge IA, rédige un plan jour par jour.",
      duration: "Durée (jours)",
      travelers: "Voyageurs",
      arrival: "Ville d'arrivée",
      budget: "Budget",
      budgets: { budget: "Économique", mid: "Milieu de gamme", luxury: "Luxe" },
      interests: "Centres d'intérêt",
      interestOptions: ["Histoire", "Gastronomie", "Nature", "Villes modernes", "Shopping", "Spirituel", "Aventure", "Famille"],
      notes: "Autre chose ? (facultatif)",
      submit: "Générer mon itinéraire",
      generating: "Création de votre voyage...",
      result: "Votre itinéraire",
      reset: "Recommencer",
    },
    contact: {
      eyebrow: "Contactez-nous",
      title: "Parlez à un curateur de voyages en Chine",
      subtitle: "Voyages sur mesure, groupes, affaires — écrivez-nous, réponse sous 24 h.",
      name: "Nom complet",
      email: "Adresse e-mail",
      subject: "Sujet",
      message: "Votre message",
      submit: "Envoyer la demande",
      success: "Merci. Mei vous contactera bientôt.",
      error: "Une erreur s'est produite. Veuillez réessayer.",
    },
    chat: {
      title: "Demander à Mei",
      subtitle: "Concierge de voyage IA",
      placeholder: "Où aller ? Que manger ?",
      send: "Envoyer",
      welcome: "Bonjour ! Je suis Mei, votre guide en Chine. Posez-moi vos questions ou demandez un itinéraire.",
    },
    footer: {
      tagline: "The China Explorer — cinq mille ans de merveilles, votre guide personnel.",
      rights: "© 2026 The China Explorer. Tous droits réservés.",
    },
  },

  es: {
    nav: {
      destinations: "Destinos",
      culture: "Cultura",
      cuisine: "Cocina",
      festivals: "Festivales",
      tips: "Consejos",
      visa: "Visado",
      guide: "Guía",
      services: "Servicios",
      cantonFair: "Feria de Cantón",
      itinerary: "Itinerario",
      book: "Reservar",
      contact: "Contacto",
      whyUs: "Por qué nosotros",
      experiences: "Experiencias",
      education: "Educación",
      faq: "FAQ",

    },
    hero: {
      eyebrow: "Descubre el Reino del Medio",
      title: "Una civilización antigua,\nuna maravilla moderna.",
      subtitle:
        "De los karsts brumosos de Guilin al horizonte de neón de Shanghái — cinco mil años de cultura ante ti.",
      cta_primary: "Explorar destinos",
      cta_secondary: "Planear mi viaje",
    },
    destinations: {
      eyebrow: "Destinos icónicos",
      title: "Donde nacen las leyendas",
      items: [
        { name: "La Gran Muralla", city: "Pekín", blurb: "Un dragón de piedra de 21 000 km serpenteando por la historia." },
        { name: "Ciudad Prohibida", city: "Pekín", blurb: "500 años de secretos imperiales tras muros carmesí." },
        { name: "Guilin y Río Li", city: "Guangxi", blurb: "Montañas kársticas brumosas que inspiraron la pintura a tinta." },
        { name: "El Bund, Shanghái", city: "Shanghái", blurb: "Elegancia colonial frente a un horizonte futurista." },
        { name: "Ejército de Terracota", city: "Xi'an", blurb: "El ejército silencioso de un emperador, ocho mil soldados." },
        { name: "Zhangjiajie", city: "Hunan", blurb: "Pilares de arenisca flotantes, inspiración de Avatar." },
      ],
    },
    culture: {
      eyebrow: "Patrimonio cultural",
      title: "El arte de vivir, refinado durante milenios",
      intro:
        "Caligrafía, té, ópera, kung-fu, seda — cada tradición es una puerta a la filosofía y gracia chinas.",
      pillars: [
        { name: "Ceremonia del té", body: "Jazmín, pu'er y longjing en casas de té centenarias." },
        { name: "Caligrafía", body: "Danza de pincel, tinta y papel de arroz — meditación visible." },
        { name: "Kung-fu", body: "Entrena donde los monjes de Shaolin convirtieron la disciplina en arte." },
        { name: "Ópera de Pekín", body: "Máscaras pintadas, voces vibrantes e historias que cruzan dinastías." },
      ],
    },
    cuisine: {
      eyebrow: "Joyas gastronómicas",
      title: "Ocho grandes tradiciones, un festín sin fin",
      items: [
        { name: "Sichuan", flavor: "Picante y adormecedor", dish: "Mapo tofu" },
        { name: "Cantonesa", flavor: "Delicada y fresca", dish: "Dim sum" },
        { name: "Pekinesa", flavor: "Imperial y ahumada", dish: "Pato laqueado" },
        { name: "Shanghainesa", flavor: "Dulce y salada", dish: "Xiaolongbao" },
        { name: "Hunan", flavor: "Audaz y ardiente", dish: "Cerdo del Presidente" },
        { name: "Xinjiang", flavor: "Aromática y a la brasa", dish: "Brochetas de cordero" },
      ],
    },
    festivals: {
      eyebrow: "Festivales y tradiciones",
      title: "Un año iluminado por linternas",
      items: [
        { name: "Fiesta de Primavera", when: "Ene – Feb", body: "Linternas rojas, dumplings y fuegos para el Año Nuevo lunar." },
        { name: "Fiesta de los Faroles", when: "Feb", body: "Linternas celestes flotando sobre ciudades antiguas." },
        { name: "Botes Dragón", when: "Jun", body: "Botes pintados corriendo por los ríos al ritmo de tambores." },
        { name: "Medio Otoño", when: "Sep", body: "Familias comparten tortas de luna bajo la luna de cosecha." },
      ],
    },
    tips: {
      eyebrow: "Para viajeros extranjeros",
      title: "Viaja por China sin esfuerzo",
      items: [
        { title: "Visado", body: "Muchas nacionalidades disfrutan de tránsito sin visa de 144 horas — consulta con la embajada." },
        { title: "Pagos", body: "Alipay y WeChat Pay aceptan tarjetas extranjeras — configúralas antes de volar." },
        { title: "Trenes", body: "Trenes bala conectan más de 550 ciudades. Reserva directamente con The China Explorer — mejores tarifas y soporte bilingüe." },
        { title: "Idioma", body: "Descarga una app de traducción; el inglés es común en aeropuertos y hoteles de lujo." },
        { title: "Conectividad", body: "Compra una eSIM o SIM local en el aeropuerto — y una VPN para apps habituales." },
        { title: "Etiqueta", body: "Saluda con una sonrisa, recibe tarjetas con ambas manos, no claves los palillos." },
      ],
    },
    canton: {
      eyebrow: "Viaje de negocios",
      title: "El Paquete Feria de Cantón",
      subtitle:
        "La mayor feria comercial de China, dos veces al año en Guangzhou. Diseñamos viajes llave en mano.",
      bullets: [
        "Traslados y hoteles 4–5★ cerca de Pazhou",
        "Reuniones con proveedores pre-agendadas",
        "Intérpretes bilingües (EN / 中文 / عربي)",
        "Visitas a fábricas en el delta del Río Perla",
      ],
      cta: "Solicitar cotización",
    },
    services: {
      eyebrow: "Viajes especializados",
      title: "Más que turismo",
      subtitle:
        "Además del ocio, The China Explorer diseña viajes a medida para salud, aprendizaje y negocios.",
      cta: "Consultar",
      items: [
        {
          name: "Viaje médico",
          body: "Hospitales de clase mundial y retiros de medicina tradicional — desde cardiología en Pekín hasta acupuntura en Hangzhou.",
          features: [
            "Alianzas con hospitales JCI",
            "Enlaces médicos en árabe",
            "Visado, hotel de recuperación y acompañante",
          ],
        },
        {
          name: "Viaje de mandarín",
          body: "Programas inmersivos en Pekín, Shanghái y Chengdu — ideales para estudiantes, ejecutivos o curiosos.",
          features: [
            "Clases grupales, privadas o empresariales",
            "Alojamiento en familia o campus",
            "Preparación HSK disponible",
          ],
        },
        {
          name: "Reuniones de negocios",
          body: "Viajes B2B integrales: negociaciones, auditorías, visitas a fábricas — fuera del calendario de la Feria de Cantón.",
          features: [
            "Fabricantes pre-verificados",
            "Traductores e intermediación contractual",
            "Logística en deltas del Río Perla y Yangtsé",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "Constructor IA",
      title: "Tu viaje a China, diseñado en segundos",
      subtitle: "Cuéntanos fechas, intereses y presupuesto — Mei, nuestra conserje IA, redacta un plan día a día.",
      duration: "Duración (días)",
      travelers: "Viajeros",
      arrival: "Ciudad de llegada",
      budget: "Presupuesto",
      budgets: { budget: "Económico", mid: "Medio", luxury: "Lujo" },
      interests: "Intereses",
      interestOptions: ["Historia", "Gastronomía", "Naturaleza", "Ciudades modernas", "Compras", "Espiritual", "Aventura", "Familia"],
      notes: "¿Algo más? (opcional)",
      submit: "Generar mi itinerario",
      generating: "Creando tu viaje...",
      result: "Tu itinerario",
      reset: "Empezar de nuevo",
    },
    contact: {
      eyebrow: "Ponte en contacto",
      title: "Habla con un curador de viajes a China",
      subtitle: "Viajes a medida, grupos, negocios — respondemos en 24 horas.",
      name: "Nombre completo",
      email: "Correo electrónico",
      subject: "Asunto",
      message: "Tu mensaje",
      submit: "Enviar consulta",
      success: "¡Gracias! Mei te contactará pronto.",
      error: "Algo salió mal. Inténtalo de nuevo.",
    },
    chat: {
      title: "Pregunta a Mei",
      subtitle: "Conserje de viajes IA",
      placeholder: "¿A dónde ir? ¿Qué comer?",
      send: "Enviar",
      welcome: "¡Hola! Soy Mei, tu guía en China. Pregúntame por destinos, gastronomía o pide un itinerario.",
    },
    footer: {
      tagline: "The China Explorer — cinco mil años de maravillas, tu guía personal.",
      rights: "© 2026 The China Explorer. Todos los derechos reservados.",
    },
  },

  de: {
    nav: {
      destinations: "Reiseziele",
      culture: "Kultur",
      cuisine: "Küche",
      festivals: "Feste",
      tips: "Reisetipps",
      visa: "Visum",
      guide: "Reise-Guide",
      services: "Services",
      cantonFair: "Kanton-Messe",
      itinerary: "Reiseplan",
      book: "Buchen",
      contact: "Kontakt",
      whyUs: "Warum wir",
      experiences: "Erlebnisse",
      education: "Bildung",
      faq: "FAQ",

    },
    hero: {
      eyebrow: "Entdecken Sie das Reich der Mitte",
      title: "Eine alte Zivilisation,\nein modernes Wunder.",
      subtitle:
        "Von den nebligen Karstbergen Guilins zur Neon-Skyline Shanghais — fünftausend Jahre Kultur entfalten sich.",
      cta_primary: "Ziele entdecken",
      cta_secondary: "Reise planen",
    },
    destinations: {
      eyebrow: "Ikonische Reiseziele",
      title: "Wo Legenden Gestalt annehmen",
      items: [
        { name: "Chinesische Mauer", city: "Peking", blurb: "Ein 21 000 km langer Steindrachen durch die Geschichte." },
        { name: "Verbotene Stadt", city: "Peking", blurb: "500 Jahre kaiserliche Geheimnisse hinter purpurroten Mauern." },
        { name: "Guilin & Li-Fluss", city: "Guangxi", blurb: "Nebelige Karstberge, die die Tuschmalerei inspirierten." },
        { name: "Der Bund, Shanghai", city: "Shanghai", blurb: "Koloniale Eleganz trifft auf futuristische Skyline." },
        { name: "Terrakotta-Armee", city: "Xi'an", blurb: "Die stille Armee eines Kaisers, achttausend Krieger stark." },
        { name: "Zhangjiajie", city: "Hunan", blurb: "Schwebende Sandsteinsäulen aus dem Herzen von Avatar." },
      ],
    },
    culture: {
      eyebrow: "Kulturerbe",
      title: "Die Kunst zu leben, über Jahrtausende verfeinert",
      intro:
        "Kalligrafie, Tee, Oper, Kung-Fu, Seide — jede Tradition öffnet die Tür zur chinesischen Philosophie.",
      pillars: [
        { name: "Teezeremonie", body: "Jasmin, Pu-Erh und Longjing in jahrhundertealten Teehäusern." },
        { name: "Kalligrafie", body: "Der Tanz von Pinsel, Tusche und Reispapier — sichtbare Meditation." },
        { name: "Kung-Fu", body: "Trainiere dort, wo Shaolin-Mönche Disziplin zur Kunst machten." },
        { name: "Peking-Oper", body: "Bemalte Masken, schwindelnde Stimmen, Geschichten durch Dynastien." },
      ],
    },
    cuisine: {
      eyebrow: "Kulinarische Höhepunkte",
      title: "Acht große Traditionen, ein endloses Fest",
      items: [
        { name: "Sichuan", flavor: "Scharf & betäubend", dish: "Mapo Tofu" },
        { name: "Kantonesisch", flavor: "Fein & frisch", dish: "Dim Sum" },
        { name: "Peking", flavor: "Kaiserlich & rauchig", dish: "Pekingente" },
        { name: "Shanghai", flavor: "Süß & herzhaft", dish: "Xiaolongbao" },
        { name: "Hunan", flavor: "Mutig & feurig", dish: "Schweinefleisch des Vorsitzenden" },
        { name: "Xinjiang", flavor: "Aromatisch & gegrillt", dish: "Lammspieße" },
      ],
    },
    festivals: {
      eyebrow: "Feste & Traditionen",
      title: "Ein Jahr im Licht der Laternen",
      items: [
        { name: "Frühlingsfest", when: "Jan – Feb", body: "Rote Laternen, Dumplings und Feuerwerk begrüßen das Mondneujahr." },
        { name: "Laternenfest", when: "Feb", body: "Himmelslaternen treiben über alten Städten zum Ende der Feiern." },
        { name: "Drachenboot", when: "Juni", body: "Bemalte Boote jagen Flüsse zu donnerndem Trommelschlag." },
        { name: "Mittherbstfest", when: "Sept", body: "Familien teilen Mondkuchen unter dem Erntemond." },
      ],
    },
    tips: {
      eyebrow: "Für ausländische Reisende",
      title: "China mühelos bereisen",
      items: [
        { title: "Visum", body: "Viele Nationen genießen 144-Stunden visumfreien Transit — bei der Botschaft prüfen." },
        { title: "Bezahlen", body: "Alipay & WeChat Pay akzeptieren ausländische Karten — vorher einrichten." },
        { title: "Züge", body: "Hochgeschwindigkeitszüge verbinden über 550 Städte. Buchen Sie direkt bei The China Explorer — bessere Tarife und zweisprachige Betreuung." },
        { title: "Sprache", body: "Übersetzungs-App installieren; Englisch in Flughäfen und Luxushotels üblich." },
        { title: "Verbindung", body: "eSIM oder lokale SIM am Flughafen — und ein VPN für gewohnte Apps." },
        { title: "Etikette", body: "Mit einem Lächeln grüßen, Visitenkarten beidhändig annehmen, Stäbchen nie senkrecht stecken." },
      ],
    },
    canton: {
      eyebrow: "Geschäftsreise",
      title: "Das Kanton-Messe Paket",
      subtitle:
        "Chinas größte Handelsmesse, zweimal jährlich in Guangzhou. Komplette Reisen für Käufer und Aussteller.",
      bullets: [
        "Flughafentransfers & 4–5★ Hotels in Pazhou-Nähe",
        "Vorab arrangierte Lieferantentermine",
        "Zweisprachige Dolmetscher (EN / 中文 / عربي)",
        "Werksbesuche im Perlflussdelta",
      ],
      cta: "Kanton-Messe Angebot anfordern",
    },
    services: {
      eyebrow: "Spezialreisen",
      title: "Mehr als Tourismus",
      subtitle:
        "Neben Freizeitreisen gestaltet The China Explorer maßgeschneiderte Reisen für Gesundheit, Bildung und Business.",
      cta: "Anfragen",
      items: [
        {
          name: "Medizinische Reise",
          body: "Weltklasse-Kliniken und TCM-Retreats — von Kardiologie in Peking bis Akupunktur in Hangzhou.",
          features: [
            "JCI-akkreditierte Klinik-Partnerschaften",
            "Medizinische Betreuung auf Arabisch",
            "Visum, Erholungshotel & Begleitperson",
          ],
        },
        {
          name: "Mandarin Sprachreise",
          body: "Immersive Mandarin-Programme in Peking, Shanghai und Chengdu — für Studierende, Führungskräfte oder Neugierige.",
          features: [
            "Gruppen-, Einzel- oder Business-Kurse",
            "Gastfamilie oder Campus-Unterkunft",
            "HSK-Vorbereitung verfügbar",
          ],
        },
        {
          name: "Geschäftstreffen",
          body: "Komplette B2B-Reisen: Verhandlungen, Lieferanten-Audits, Werksbesichtigungen — außerhalb der Kanton-Messe-Termine.",
          features: [
            "Vorgeprüfte Hersteller-Vermittlung",
            "Zweisprachige Übersetzer & Vertragsbetreuung",
            "Logistik in Perl- & Jangtse-Delta",
          ],
        },
      ],
    },
    itinerary: {
      eyebrow: "KI Reiseplaner",
      title: "Ihre China-Reise, in Sekunden entworfen",
      subtitle: "Nennen Sie Daten, Interessen und Budget — Mei, unsere KI-Concierge, erstellt einen Tagesplan.",
      duration: "Dauer (Tage)",
      travelers: "Reisende",
      arrival: "Ankunftsstadt",
      budget: "Budget",
      budgets: { budget: "Sparsam", mid: "Mittel", luxury: "Luxus" },
      interests: "Interessen",
      interestOptions: ["Geschichte", "Essen", "Natur", "Moderne Städte", "Shopping", "Spirituell", "Abenteuer", "Familie"],
      notes: "Sonstiges? (optional)",
      submit: "Reiseplan erstellen",
      generating: "Ihre Reise wird gestaltet...",
      result: "Ihr Reiseplan",
      reset: "Neu starten",
    },
    contact: {
      eyebrow: "Kontakt aufnehmen",
      title: "Sprechen Sie mit einem China-Reisekurator",
      subtitle: "Maßgeschneiderte Reisen, Gruppenreisen, Geschäftsreisen — Antwort in 24 Stunden.",
      name: "Vollständiger Name",
      email: "E-Mail-Adresse",
      subject: "Betreff",
      message: "Ihre Nachricht",
      submit: "Anfrage senden",
      success: "Danke. Mei meldet sich in Kürze.",
      error: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
    },
    chat: {
      title: "Mei fragen",
      subtitle: "KI-Reise-Concierge",
      placeholder: "Wohin reisen? Was essen?",
      send: "Senden",
      welcome: "Hallo! Ich bin Mei, Ihr Guide für China. Fragen Sie mich nach Zielen, Küche, Kultur — oder lassen Sie mich einen Reiseplan erstellen.",
    },
    footer: {
      tagline: "The China Explorer — fünftausend Jahre Wunder, Ihr persönlicher Guide.",
      rights: "© 2026 The China Explorer. Alle Rechte vorbehalten.",
    },
  },
};
